/**
 * ApprovePage — Página mobile para aprovar/negar/bloquear solicitações de acesso.
 *
 * Rota: /security/approve/:id
 * Aberta via push notification no celular vinculado. (Atualizado: 2026-06-24)
 */
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ShieldCheck, ShieldX, Ban, Monitor, Globe, MapPin,
  Clock, Loader2, CheckCircle2, XCircle, AlertTriangle, Fingerprint,
  Smartphone, ArrowRight, Key, Calendar,
} from 'lucide-react';
import { startAuthentication } from '@simplewebauthn/browser';
import { audioAlerts } from '../../utils/audioAlerts';
import { getRedirectPath } from '../../auth/routes';
import { clearClientSession } from '../../auth/sessionCleanup';
import {
  consumeApprovalHandoff,
  getApprovalStatus,
  respondApproval,
  verifyApprovalSession,
} from '../../security/deviceApprovalApi';
import { getSafeApprovalErrorMessage } from '../../security/approval';
import { getPasskeyFailureMessage, isPasskeyDialogCancel } from '../../security/login/passkey.failurePolicy';
import {
  requestPasskeyAuthOptions,
  verifyPasskeyAuthentication,
} from '../../security/passkey/passkeyApi';

type Status = 'loading' | 'ready' | 'responding' | 'done' | 'error' | 'expired';

interface SolicitacaoData {
  id: string;
  tipo: string;
  status: string;
  contexto: any;
  expiraEm: string;
  criadoEm: string;
  dezenaCorreta?: string | null;
  dezenasOpcoes?: string | null;
}

const numMap: Record<string, string> = {
  'dez': '10', 'onze': '11', 'doze': '12', 'treze': '13', 'catorze': '14', 'quatorze': '14', 'quinze': '15',
  'dezesseis': '16', 'dezessete': '17', 'dezoito': '18', 'dezenove': '19', 'vinte': '20',
  'trinta': '30', 'quarenta': '40', 'cinquenta': '50', 'sessenta': '60', 'setenta': '70',
  'oitenta': '80', 'noventa': '90'
};

const unidadesMap: Record<string, number> = {
  'um': 1, 'uma': 1, 'dois': 2, 'duas': 2, 'três': 3, 'quatro': 4, 'cinco': 5, 'seis': 6, 'sete': 7, 'oito': 8, 'nove': 9
};

function normalizeSpokenNumber(text: string): string {
  const clean = text.toLowerCase().trim().replace(/[^a-z0-9\s]/g, '');
  if (/^\d+$/.test(clean)) return clean;
  
  const parts = clean.split(/\s+e\s+|\s+/);
  if (parts.length === 1) {
    return numMap[parts[0]] || clean;
  }
  if (parts.length === 2 || parts.length === 3) {
    const dezenaStr = numMap[parts[0]];
    const unidadeStr = parts[parts.length - 1];
    const dezVal = dezenaStr ? parseInt(dezenaStr, 10) : 0;
    const uniVal = unidadesMap[unidadeStr] || (parseInt(unidadeStr, 10) || 0);
    if (dezVal > 0) {
      return (dezVal + uniVal).toString();
    }
  }
  return clean;
}

function cleanBrowserName(raw: string | undefined | null): string {
  if (!raw) return 'Dispositivo';
  const lower = raw.toLowerCase();
  if (lower.includes('chrome') || lower.includes('google chrome')) return 'Google Chrome';
  if (lower.includes('firefox')) return 'Mozilla Firefox';
  if (lower.includes('safari') && !lower.includes('chrome')) return 'Safari';
  if (lower.includes('edge') || lower.includes('edg/')) return 'Microsoft Edge';
  if (lower.includes('opera') || lower.includes('opr/')) return 'Opera';
  if (lower.includes('chromium')) return 'Chromium';
  if (raw.includes('";v="') || raw.includes(';v=')) {
    const parts = raw.split(',').map(p => p.trim().replace(/^"/, '').replace(/"$/, ''));
    const cleanParts = parts.filter(p => !p.toLowerCase().includes('not') && !p.toLowerCase().includes('brand'));
    if (cleanParts.length > 0) {
      const first = cleanParts[0].split(';')[0];
      return first.replace(/"/g, '');
    }
  }
  return raw;
}

//#region AC_BLOCK approve-page.approve-page -- Aprovacao mobile de seguranca
// @ac-purpose Consome handoff de aprovacao, exige PWA/mobile quando aplicavel, dispara biometria/passkey e responde solicitacoes de acesso.
// @ac-related react-app/src/App.tsx#ApprovePage
// @ac-related react-app/src/security/approval/index.ts
// @ac-related react-app/src/security/passkey/passkeyApi.ts
// @ac-related docs_secutiry/rotas-canonicas-seguranca.md#Approval-e-handoff
// @ac-routes /security/approve, /security/approve/:id, /auth/security/approval/:id/handoff/consume
// @ac-tests node tools/codex-kit.mjs check react-tsc
export function ApprovePage() {
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [id, setId] = useState<string>('');
  const [isStandalone, setIsStandalone] = useState(true);
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
  const [pageStatus, setPageStatus] = useState<Status>('loading');
  const [sol, setSol] = useState<SolicitacaoData | null>(null);
  const [resultado, setResultado] = useState<string | null>(null);
  const [showBlockMenu, setShowBlockMenu] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [autoCloseSeconds, setAutoCloseSeconds] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [opcao, setOpcao] = useState<'TEMPORARIO' | 'PERMANENTE' | 'PASSKEY'>('PASSKEY');
  const [autoPromptDone, setAutoPromptDone] = useState(false);
  const [copiedIp, setCopiedIp] = useState(false);
  const [authOptions, setAuthOptions] = useState<any>(null);
  const [showBiometriaFallback, setShowBiometriaFallback] = useState(false);
  
  const [micActive, setMicActive] = useState(false);
  const [lastHeard, setLastHeard] = useState('');
  const [sttError, setSttError] = useState('');
  const [voiceAutoPromptDone, setVoiceAutoPromptDone] = useState(false);
  const recognitionRef = useRef<any>(null);
  const userGestureTriggeredRef = useRef(false);

  // Sincronizar ou extrair ID da URL/QueryString e checar se está no PWA
  useEffect(() => {
    // Checar standalone
    const checkStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone;
    setIsStandalone(!!checkStandalone);

    if (routeId) {
      setId(routeId);
      return;
    }

    // Se não tem ID na rota, tenta extrair da querystring ?uri=...
    const searchParams = new URLSearchParams(window.location.search);
    const uriParam = searchParams.get('uri');
    if (uriParam) {
      const cleanUri = uriParam.replace(/^web\+alunoconectado:\/\//i, 'https://');
      try {
        const parsedUrl = new URL(cleanUri);
        const pathAndQuery = parsedUrl.pathname + parsedUrl.search;
        
        if (pathAndQuery.startsWith('/security/approve/')) {
          const extractedId = pathAndQuery.replace('/security/approve/', '');
          if (extractedId) {
            setId(extractedId);
            navigate(`/security/approve/${extractedId}`, { replace: true });
          }
        } else {
          // Roteamento genérico para qualquer outra página do site quando invocado via deep link!
          console.log('[PWA] Protocol handler redirecionando para:', pathAndQuery);
          navigate(pathAndQuery, { replace: true });
        }
      } catch (e) {
        console.warn('Erro ao parsear URL do protocolo customizado, usando fallback de regex:', e);
        const idMatch = uriParam.match(/([a-f0-9\-]{36})/i); // UUID
        const fallbackMatch = uriParam.match(/\/approve\/([a-zA-Z0-9_\-]+)/);
        const simpleMatch = uriParam.match(/:?\/?\/?([a-zA-Z0-9_\-]+)$/);
        
        const extractedId = idMatch ? idMatch[1] : (fallbackMatch ? fallbackMatch[1] : (simpleMatch ? simpleMatch[1] : ''));
        if (extractedId) {
          setId(extractedId);
          navigate(`/security/approve/${extractedId}`, { replace: true });
        }
      }
    }
  }, [routeId, navigate]);

  // Redirecionamento PWA para a rota central /install
  useEffect(() => {
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
    const skippedPwaInstall = localStorage.getItem('ac_skip_pwa_install') === 'true';
    if (isMobile && !isStandalone && !skippedPwaInstall && !isIOSDevice) {
      const forceBrowserLocal = sessionStorage.getItem('ac_force_browser') === 'true' || 
                                new URLSearchParams(window.location.search).get('browser') === '1';
      if (!forceBrowserLocal) {
        navigate(`/install?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`, { replace: true });
      }
    }
  }, [isMobile, isStandalone, navigate]);

  const handleOpenInApp = () => {
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
    if (isIOSDevice) return;
    try {
      const customUrl = `web+alunoconectado://${window.location.host}${window.location.pathname}${window.location.search}`;
      window.location.href = customUrl;
    } catch {
      // Ignora falhas de esquema personalizado
    }
  };

  const handleCopyIp = (ip: string) => {
    navigator.clipboard.writeText(ip)
      .then(() => {
        setCopiedIp(true);
        setTimeout(() => setCopiedIp(false), 2000);
      })
      .catch(err => console.error('Erro ao copiar IP:', err));
  };

  const token = localStorage.getItem('ac_token') || '';

  // Redirecionar se não estiver logado
  useEffect(() => {
    if (!id) return;

    const willRedirectToInstall = isMobile && !isStandalone && 
      sessionStorage.getItem('ac_force_browser') !== 'true' && 
      new URLSearchParams(window.location.search).get('browser') !== '1';
      
    if (willRedirectToInstall) return;

    if (!token) {
      console.warn('[ApprovePage] Usuário não está autenticado. Salvando destino e redirecionando para login...');
      sessionStorage.setItem(
        'ac_redirect_after_login',
        `${window.location.pathname}${window.location.search}`,
      );
      navigate('/login', { replace: true });
    }
  }, [id, token, navigate, isMobile, isStandalone]);

  // Buscar dados da solicitação
  useEffect(() => {
    if (!id || !token) return;

    const willRedirectToInstall = isMobile && !isStandalone && 
      sessionStorage.getItem('ac_force_browser') !== 'true' && 
      new URLSearchParams(window.location.search).get('browser') !== '1';
      
    if (willRedirectToInstall) {
      console.log('[ApprovePage] Evitando chamada de API porque redirecionaremos para instalação do PWA.');
      return;
    }

    const readyForAuthenticatedCalls = (async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const handoff = searchParams.get('handoff');
      if (!handoff) return;

      await consumeApprovalHandoff(id, handoff);

      searchParams.delete('handoff');
      const newSearch = searchParams.toString();
      navigate(`${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
    })();

    // Registrar como aberta para sumir do cabeçalho
    try {
      const openedStr = localStorage.getItem('ac_opened_approvals') || '[]';
      let openedIds: string[] = [];
      try {
        openedIds = JSON.parse(openedStr);
      } catch (e) {
        openedIds = [];
      }
      if (!openedIds.includes(id)) {
        openedIds.push(id);
        localStorage.setItem('ac_opened_approvals', JSON.stringify(openedIds));
      }
    } catch (e) {
      console.warn('Erro ao salvar no localStorage', e);
    }

    // Buscar status da solicitação
    readyForAuthenticatedCalls
      .then(() => getApprovalStatus(id, { bearerToken: token }))
      .then(data => {
        setSol(data);
        if (data.status !== 'PENDENTE') {
          setResultado(data.status);
          setPageStatus('done');
        } else {
          setPageStatus('ready');
          // Countdown
          const diff = Math.max(0, Math.floor((new Date(data.expiraEm).getTime() - Date.now()) / 1000));
          setCountdown(diff);
        }
      })
      .catch((err) => {
        const safeMessage = getSafeApprovalErrorMessage(err);
        setError(
          safeMessage ||
            'Este QR expirou ou ja foi utilizado. Solicite um novo acesso.',
        );
        setPageStatus('error');
      });

    // Pré-carregamento em paralelo imediato das opções de biometria para evitar latência no auto-prompt
    console.log('[ApprovePage] Pré-carregando auth-options de biometria em paralelo...');
    readyForAuthenticatedCalls
      .then(() => requestPasskeyAuthOptions({ bearerToken: token }))
      .then(opts => {
        if (opts) {
          setAuthOptions(opts);
          console.log('[ApprovePage] Opções biométricas carregadas com sucesso em paralelo.');
        }
      })
      .catch(err => {
        console.warn('[ApprovePage] Erro ao pré-carregar auth-options:', err);
        const message =
          err?.response?.data?.message ||
          err?.message ||
          'Erro ao carregar opções de biometria.';
        if (message.toLowerCase().includes('handoff')) return;
        setError(message);
        setShowBiometriaFallback(true);
      });
  }, [id]);

  // Timer countdown
  useEffect(() => {
    if (pageStatus !== 'ready' || countdown <= 0) return;
    const iv = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(iv);
          setPageStatus('expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [pageStatus, countdown]);

  const handleClose = () => {
    try {
      window.close();
    } catch (e) {
      // Ignorar erro se o navegador bloquear o fechamento programático
    }
    const token = localStorage.getItem('ac_token') || '';
    if (token) {
      const savedUserStr = localStorage.getItem('ac_user');
      if (savedUserStr) {
        try {
          const u = JSON.parse(savedUserStr);
          navigate(getRedirectPath(u.perfil || '', u.cargo || undefined), { replace: true });
          return;
        } catch {}
      }
      navigate('/dashboard', { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  };

  // Auto-close effect when reaching a terminal state
  useEffect(() => {
    if (pageStatus === 'done' || pageStatus === 'expired' || pageStatus === 'error') {
      setAutoCloseSeconds(3);
    } else {
      setAutoCloseSeconds(null);
    }
  }, [pageStatus]);

  useEffect(() => {
    if (autoCloseSeconds === null) return;
    if (autoCloseSeconds <= 0) {
      handleClose();
      return;
    }
    const timer = setTimeout(() => {
      setAutoCloseSeconds(prev => prev !== null ? prev - 1 : null);
    }, 1000);
    return () => clearTimeout(timer);
  }, [autoCloseSeconds]);

  const playRequestAlarmSound = () => {
    audioAlerts.playSolicitandoAcesso();
  };

  const enableUserGestureTrigger = () => {
    if (userGestureTriggeredRef.current) return;
    
    console.log('[ApprovePage] Registrando listener de toque global para disparar biometria no primeiro gesto...');
    
    const handleGesture = (e: Event) => {
      if (userGestureTriggeredRef.current) return;
      
      const target = e.target as HTMLElement;
      const isNegarOrBloquear = target && (
        target.closest('button')?.textContent?.includes('NEGAR') ||
        target.closest('button')?.textContent?.includes('BLOQUEAR') ||
        target.closest('button')?.textContent?.includes('Bloquear') ||
        target.closest('button')?.textContent?.includes('Cancelar')
      );
      
      if (isNegarOrBloquear) {
        console.log('[ApprovePage] Clique em Negar/Bloquear detectado, descartando gesto global.');
        document.removeEventListener('click', handleGesture);
        document.removeEventListener('touchstart', handleGesture);
        userGestureTriggeredRef.current = true;
        return;
      }
      
      userGestureTriggeredRef.current = true;
      document.removeEventListener('click', handleGesture);
      document.removeEventListener('touchstart', handleGesture);
      
      console.log('[ApprovePage] Gesto detectado! Disparando biometria...');
      responder('AUTORIZADO');
    };
    
    document.addEventListener('click', handleGesture);
    document.addEventListener('touchstart', handleGesture, { passive: true });
  };

  useEffect(() => {
    if (pageStatus === 'ready' && sol && authOptions) {
      const destravadoSessao = typeof window !== 'undefined' && sessionStorage.getItem('ac_pwa_unlocked_this_session') === 'true';
      const pularBiometriaNativa = isStandalone || destravadoSessao;
      const requerBio = (sol.tipo === 'BIOMETRIA_DIARIA' || sol.tipo === 'DEVICE_AUTH') && !pularBiometriaNativa;
      if (requerBio && !autoPromptDone) {
        setAutoPromptDone(true);
        console.log('[ApprovePage] Disparando biometria automaticamente com opções pré-carregadas...');
        const t = setTimeout(() => {
          responder('AUTORIZADO', undefined, undefined, true);
        }, 150);
        return () => clearTimeout(t);
      }
      
      const dezenaCorreta = sol.dezenaCorreta || sol.contexto?.dezenaCorreta;
      const dezenasOpcoesStr = sol.dezenasOpcoes || sol.contexto?.dezenasOpcoes;
      const requerVozDezenas = !requerBio && !!dezenaCorreta && !!dezenasOpcoesStr;
      
      if (requerVozDezenas && !voiceAutoPromptDone) {
        setVoiceAutoPromptDone(true);
        console.log('[ApprovePage] Solicitação por voz/dezenas detectada. Iniciando fluxo TTS/STT...');
        const t = setTimeout(() => {
          speakChallenge();
        }, 1000);
        return () => clearTimeout(t);
      }
    }
  }, [pageStatus, sol, authOptions, autoPromptDone, voiceAutoPromptDone]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch {}
      }
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const speakChallenge = () => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    
    const cor = sol?.dezenaCorreta || sol?.contexto?.dezenaCorreta || '';
    if (!cor) return;
    
    const utterance = new SpeechSynthesisUtterance("Qual a dezena que está na tela?");
    utterance.lang = 'pt-BR';
    utterance.rate = 1.25;
    utterance.pitch = 0.85;
    
    const voices = window.speechSynthesis.getVoices();
    const ptVoices = voices.filter(v => v.lang.startsWith('pt-') || v.lang === 'pt-BR');
    const maleVoice = ptVoices.find(v => 
      v.name.toLowerCase().includes('daniel') || 
      v.name.toLowerCase().includes('male') ||
      v.name.toLowerCase().includes('homem') ||
      v.name.toLowerCase().includes('brazil')
    );
    if (maleVoice) {
      utterance.voice = maleVoice;
    } else if (ptVoices.length > 0) {
      utterance.voice = ptVoices[0];
    }
    
    utterance.onend = () => {
      iniciarReconhecimento();
    };
    
    window.speechSynthesis.speak(utterance);
  };

  const iniciarReconhecimento = () => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) {
      setSttError('Reconhecimento de voz não suportado.');
      return;
    }
    
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch {}
    }
    
    const recognition = new SR();
    recognitionRef.current = recognition;
    recognition.lang = 'pt-BR';
    recognition.continuous = false;
    recognition.interimResults = false;
    
    recognition.onstart = () => {
      setMicActive(true);
      setSttError('');
    };
    
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setLastHeard(transcript);
      const normalized = normalizeSpokenNumber(transcript);
      console.log('[STT] Ouvido:', transcript, '-> Normalizado:', normalized);
      
      const cor = sol?.dezenaCorreta || sol?.contexto?.dezenaCorreta;
      if (normalized === cor) {
        responder('AUTORIZADO', undefined, normalized);
      } else {
        setSttError(`Você disse "${transcript}" (${normalized}), mas não corresponde à dezena da tela.`);
      }
    };
    
    recognition.onerror = (event: any) => {
      console.warn('[STT] Erro:', event.error);
      setMicActive(false);
      if (event.error === 'not-allowed') {
        setSttError('Permissão para microfone negada. Por favor, use os botões físicos.');
      } else if (event.error === 'no-speech') {
        setSttError('Nenhuma fala detectada. Clique no botão de microfone para tentar de novo.');
      } else {
        setSttError('Erro ao reconhecer voz. Use os botões físicos de fallback.');
      }
    };
    
    recognition.onend = () => {
      setMicActive(false);
    };
    
    try {
      recognition.start();
    } catch (e) {
      console.error('[STT] Falha ao iniciar:', e);
    }
  };

  useEffect(() => {
    if (pageStatus === 'ready') {
      playRequestAlarmSound();
    }
  }, [pageStatus]);

  async function responder(
    resposta: 'AUTORIZADO' | 'NEGADO' | 'BLOQUEADO',
    tipoBloqueio?: string,
    respostaDezena?: string,
    isAutoPrompt = false
  ) {
    if (!sol) return;
    
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch {}
    }
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    
    if (!isAutoPrompt) {
      setPageStatus('responding');
    }
    setError('');
    try {
      const destravadoSessao = typeof window !== 'undefined' && sessionStorage.getItem('ac_pwa_unlocked_this_session') === 'true';
      const pularBiometriaNativa = isStandalone || destravadoSessao;
      const requerBio = (sol.tipo === 'BIOMETRIA_DIARIA' || sol.tipo === 'DEVICE_AUTH') && !pularBiometriaNativa;
      
      // Se requer biometria do celular, verificar WebAuthn/Passkey local do celular antes de autorizar
      if (requerBio && resposta === 'AUTORIZADO') {
        try {
          let opts = authOptions;
          if (!opts) {
            console.log('[ApprovePage] Opções biométricas não estavam em cache. Buscando agora...');
            opts = await requestPasskeyAuthOptions({ bearerToken: token });
          }
          // Criar uma promise de timeout de 1200ms com detecção de foco para identificar bloqueio silencioso do navegador
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => {
              if (document.hasFocus()) {
                console.warn('[ApprovePage] Diálogo biométrico não detectado (janela focada). Cancelando auto-prompt silencioso.');
                reject(new Error('WebAuthnTimeout'));
              } else {
                console.log('[ApprovePage] Janela perdeu foco. Diálogo biométrico nativo está ativo.');
              }
            }, 1200);
          });

          const assertion = await Promise.race([
            startAuthentication({ optionsJSON: opts }),
            timeoutPromise
          ]) as any;

          try {
            await verifyPasskeyAuthentication(assertion, { bearerToken: token });
          } catch (verifyErr: any) {
            throw new Error(
              getPasskeyFailureMessage(
                verifyErr?.response?.data || verifyErr,
                'Validação biométrica inválida.',
              ),
            );
          }
        } catch (authErr: any) {
          console.warn('[ApprovePage] Falha na biometria do celular:', authErr);
          
          const isCancelation = isPasskeyDialogCancel(authErr) || authErr?.message === 'WebAuthnTimeout';
          
          if (isAutoPrompt && isCancelation) {
            // Se foi auto prompt e o navegador bloqueou por falta de gesto ou o usuário cancelou o prompt, silenciar e voltar a ready
            setError('');
            setPageStatus('ready');
            setShowBiometriaFallback(true);
            enableUserGestureTrigger();
            return;
          }
          
          // Se for cancelamento explícito do usuário, apenas repassa o erro para não revogar
          if (isCancelation) {
            throw new Error('Operação biométrica cancelada pelo usuário.');
          }
          
          const safeBiometryFallbackMessage =
            'Nao foi possivel confirmar a biometria agora. Sua passkey foi preservada; use o cadastro de seguranca ou tente outra forma segura.';
          setShowBiometriaFallback(true);
          setError(safeBiometryFallbackMessage);
          console.warn('[ApprovePage] Falha WebAuthn preservou passkey local e nao acionou revogacao global.');

          // Verificar silenciosamente se a sessão do usuário no servidor ainda é válida
          let isSessionValid = false;
          try {
            await verifyApprovalSession({ bearerToken: token });
            isSessionValid = true;
          } catch (meErr) {
            console.error('[ApprovePage] Falha ao verificar autenticação:', meErr);
          }

          if (isSessionValid) {
            console.log('[ApprovePage] Sessão válida. Redirecionando para cadastro de seguranca sem revogar passkeys...');
            localStorage.setItem('ac_security_onboarding_pending', '1');
            navigate('/onboarding/security', { replace: true });
            return;
          } else {
            console.log('[ApprovePage] Sessão inválida ou expirada. Limpando apenas sessao local e redirecionando para login...');
            try {
              await clearClientSession();
            } catch {}
            navigate('/login', { replace: true });
            return;
          }
        }
      }

      await respondApproval(
        {
          solicitacaoId: sol.id,
          resposta,
          tipoBloqueio: tipoBloqueio as any,
          opcao: resposta === 'AUTORIZADO' ? opcao : undefined,
          respostaDezena,
        },
        { bearerToken: token },
      );
      setResultado(resposta);
      setPageStatus('done');
      if (resposta === 'AUTORIZADO') {
        audioAlerts.playAcessoAutorizado();
      }
    } catch (e: any) {
      console.warn('[ApprovePage] Erro ao responder:', e);
      const isCancelation = e?.name === 'NotAllowedError' || 
                            e?.name === 'AbortError' || 
                            e?.message?.includes('cancelada pelo usuário') || 
                            e?.message === 'WebAuthnTimeout';
      
      if (!isAutoPrompt || !isCancelation) {
        setError(e.message || 'Falha na autenticação biométrica ou operação cancelada.');
      } else {
        setError('');
      }
      setPageStatus('ready');
      if (resposta === 'AUTORIZADO') {
        setShowBiometriaFallback(true);
      }
      
      const dezenaCorreta = sol?.dezenaCorreta || sol?.contexto?.dezenaCorreta;
      const dezenasOpcoesStr = sol?.dezenasOpcoes || sol?.contexto?.dezenasOpcoes;
      if (resposta === 'AUTORIZADO' && !!dezenaCorreta && !!dezenasOpcoesStr) {
        setTimeout(() => speakChallenge(), 1000);
      }
    }
  }

  // ── RENDERS ──

  // Se for celular e não for standalone (não PWA instalado), exige instalar o PWA primeiro
  if (isMobile && !isStandalone) {
    const forceBrowserLocal = sessionStorage.getItem('ac_force_browser') === 'true' || 
                              new URLSearchParams(window.location.search).get('browser') === '1';
    if (!forceBrowserLocal) {
      return (
        <div style={pageStyle}>
          <div className="absolute -top-20 -left-20 w-40 h-40 bg-[#a855f7]/20 blur-[100px] -z-10 animate-pulse" />
          <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-[#3b82f6]/20 blur-[100px] -z-10 animate-pulse" />
          <main className="relative z-10 w-full max-w-md flex flex-col items-center justify-center lg:w-[420px] px-4">
            <div className="glass-panel flex flex-col items-center justify-center p-8 rounded-[2rem] text-center w-full">
              <Loader2 size={36} className="text-[#a855f7] animate-spin mb-4" />
              <p className="text-[#e5e2e1] font-semibold">Redirecionando para a instalação do PWA...</p>
            </div>
          </main>
          <style>{`
            .glass-panel {
              background: linear-gradient(145deg, rgba(30, 30, 30, 0.9) 0%, rgba(15, 15, 15, 0.95) 100%);
              backdrop-filter: blur(24px);
              border: 1px solid rgba(255, 255, 255, 0.08);
              box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
            }
          `}</style>
        </div>
      );
    }
  }

  if (pageStatus === 'loading' || pageStatus === 'responding') {
    const isResponding = pageStatus === 'responding';
    return (
      <div style={pageStyle}>
        <div className="absolute -top-20 -left-20 w-40 h-40 bg-[#a855f7]/20 blur-[100px] -z-10 animate-pulse" />
        <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-[#3b82f6]/20 blur-[100px] -z-10 animate-pulse" />
        <main className="relative z-10 w-full max-w-md flex flex-col items-center justify-center lg:w-[420px] px-4">
          <div className="glass-panel flex flex-col items-center justify-center p-8 rounded-[2rem] text-center w-full">
            <Loader2 size={36} className="text-[#a855f7] animate-spin mb-4" />
            <p className="text-[#e5e2e1] font-semibold">{isResponding ? 'Processando...' : 'Carregando...'}</p>
          </div>
        </main>
        <style>{`
          .glass-panel {
            background: linear-gradient(145deg, rgba(30, 30, 30, 0.9) 0%, rgba(15, 15, 15, 0.95) 100%);
            backdrop-filter: blur(24px);
            border: 1px solid rgba(255, 255, 255, 0.08);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
          }
        `}</style>
      </div>
    );
  }

  if (pageStatus === 'error') {
    return (
      <div style={pageStyle}>
        <div className="absolute -top-20 -left-20 w-40 h-40 bg-[#a855f7]/20 blur-[100px] -z-10" />
        <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-[#3b82f6]/20 blur-[100px] -z-10" />
        <main className="relative z-10 w-full max-w-md flex flex-col items-center justify-center lg:w-[420px] px-4">
          <div className="glass-panel flex flex-col items-center justify-center p-8 rounded-[2rem] text-center w-full space-y-4">
            <div className="p-3 rounded-full bg-red-500/10 border border-red-500/20 shadow-[0_0_15px_rgba(239,68,68,0.2)]">
              <AlertTriangle size={36} className="text-[#ef4444]" />
            </div>
            <h2 className="text-lg font-bold text-white uppercase tracking-wider">Falha de Solicitação</h2>
            <p className="text-[#fca5a5] text-sm font-semibold max-w-xs">{error}</p>
            <button onClick={handleClose} className="w-full bg-white/10 hover:bg-white/20 text-[#e5e2e1] font-bold py-3.5 rounded-xl active:scale-95 transition-all border border-white/5 text-sm uppercase tracking-wider flex items-center justify-center gap-2">
              <span>Voltar ao Início</span>
              {autoCloseSeconds !== null && (
                <span className="text-xs opacity-60 font-mono">({autoCloseSeconds}s)</span>
              )}
            </button>
          </div>
        </main>
        <style>{`
          .glass-panel {
            background: linear-gradient(145deg, rgba(30, 30, 30, 0.9) 0%, rgba(15, 15, 15, 0.95) 100%);
            backdrop-filter: blur(24px);
            border: 1px solid rgba(255, 255, 255, 0.08);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
          }
        `}</style>
      </div>
    );
  }

  if (pageStatus === 'expired') {
    return (
      <div style={pageStyle}>
        <div className="absolute -top-20 -left-20 w-40 h-40 bg-[#a855f7]/20 blur-[100px] -z-10" />
        <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-[#3b82f6]/20 blur-[100px] -z-10" />
        <main className="relative z-10 w-full max-w-md flex flex-col items-center justify-center lg:w-[420px] px-4">
          <div className="glass-panel flex flex-col items-center justify-center p-8 rounded-[2rem] text-center w-full space-y-4">
            <div className="p-3 rounded-full bg-yellow-50 border border-yellow-100">
              <Clock size={36} className="text-yellow-500" />
            </div>
            <h2 className="text-lg font-bold text-[#0f172a] uppercase tracking-wider">Tempo Esgotado</h2>
            <p className="text-[#64748b] text-sm max-w-xs">
              A solicitação expirou. Se necessário, tente novamente no computador.
            </p>
            <button onClick={handleClose} className="w-full bg-[#f1f5f9] hover:bg-[#e2e8f0] text-[#334155] font-bold py-3.5 rounded-xl active:scale-95 transition-all border border-slate-200/50 text-sm uppercase tracking-wider flex items-center justify-center gap-2">
              <span>Voltar ao Início</span>
              {autoCloseSeconds !== null && (
                <span className="text-xs opacity-60 font-mono">({autoCloseSeconds}s)</span>
              )}
            </button>
          </div>
        </main>
        <style>{`
          .glass-panel {
            background: #ffffff;
            border: 1px solid rgba(0, 0, 0, 0.08);
            box-shadow: 0 25px 60px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04);
          }
        `}</style>
      </div>
    );
  }

  if (pageStatus === 'done') {
    const isOk = resultado === 'AUTORIZADO';
    const isBlock = resultado === 'BLOQUEADO';
    return (
      <div style={pageStyle}>
        <div className="absolute -top-20 -left-20 w-40 h-40 bg-[#a855f7]/20 blur-[100px] -z-10" />
        <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-[#3b82f6]/20 blur-[100px] -z-10" />
        <main className="relative z-10 w-full max-w-md flex flex-col items-center justify-center lg:w-[420px] px-4">
          <div className="glass-panel flex flex-col items-center justify-center p-8 rounded-[2rem] text-center w-full space-y-4">
            <div className={`p-4 rounded-full border ${
              isOk 
                ? 'bg-emerald-500/10 border-emerald-500/20 shadow-[0_0_20px_rgba(16,185,129,0.3)]' 
                : 'bg-red-500/10 border-red-500/20 shadow-[0_0_20px_rgba(239,68,68,0.3)]'
            }`}>
              {isOk ? <CheckCircle2 size={40} className="text-[#10b981]" />
                    : isBlock ? <Ban size={40} className="text-[#ef4444]" />
                    : <XCircle size={40} className="text-[#ef4444]" />}
            </div>
            <h2 className={`text-xl font-bold uppercase tracking-wider ${isOk ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>
              {isOk ? 'Acesso Autorizado' : isBlock ? 'Dispositivo Bloqueado' : 'Acesso Negado'}
            </h2>
            <p className="text-[#64748b] text-sm max-w-xs">
              {isOk ? 'O computador foi liberado. Pode fechar esta tela.'
                    : isBlock ? 'O dispositivo foi registrado como ameaça.'
                    : 'A tentativa de acesso foi rejeitada.'}
            </p>
            <button onClick={handleClose} className="w-full bg-[#f1f5f9] hover:bg-[#e2e8f0] text-[#334155] font-bold py-3.5 rounded-xl active:scale-95 transition-all border border-slate-200/50 text-sm uppercase tracking-wider flex items-center justify-center gap-2">
              <span>Voltar ao Início</span>
              {autoCloseSeconds !== null && (
                <span className="text-xs opacity-60 font-mono">({autoCloseSeconds}s)</span>
              )}
            </button>
          </div>
        </main>
        <style>{`
          .glass-panel {
            background: #ffffff;
            border: 1px solid rgba(0, 0, 0, 0.08);
            box-shadow: 0 25px 60px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04);
          }
        `}</style>
      </div>
    );
  }

  // ── READY — Tela principal de aprovação ──
  const destravadoSessao = typeof window !== 'undefined' && sessionStorage.getItem('ac_pwa_unlocked_this_session') === 'true';
  const pularBiometriaNativa = isStandalone || destravadoSessao;

  const isFluxoBiometrico = (sol?.tipo === 'BIOMETRIA_DIARIA' || sol?.tipo === 'DEVICE_AUTH') && !pularBiometriaNativa;
  const dezenaCorreta = sol?.dezenaCorreta || sol?.contexto?.dezenaCorreta;
  const dezenasOpcoesStr = sol?.dezenasOpcoes || sol?.contexto?.dezenasOpcoes;
  const dezenasOpcoes: string[] = dezenasOpcoesStr ? dezenasOpcoesStr.split(',') : [];
  const requerVozDezenas = !isFluxoBiometrico && !!dezenaCorreta && dezenasOpcoes.length > 0;
  
  const radius = 42;
  const circumference = 2 * Math.PI * radius; // 263.89
  const strokeDashoffset = circumference - (countdown / 60) * circumference;
  const ctx = sol?.contexto || {};

  return (
    <div style={pageStyle}>
      {/* Decorative subtle lights */}
      <div className="absolute -top-20 -left-20 w-40 h-40 bg-[#a855f7]/20 blur-[100px] -z-10" />
      <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-[#3b82f6]/20 blur-[100px] -z-10" />

      <main className="relative z-10 w-full max-w-md mx-auto flex flex-col lg:w-[420px] px-4">
        {!isStandalone && (
          <div style={appBannerStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={appIconStyle}>
                <ShieldCheck size={20} color="#a78bfa" />
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 800, color: '#f8fafc' }}>Abrir no Aplicativo</div>
                <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>Para uma experiência mais rápida e segura</div>
              </div>
            </div>
            <button
              onClick={handleOpenInApp}
              style={appBannerBtnStyle}
            >
              Abrir
            </button>
          </div>
        )}

        {/* Header */}
        <header className="flex flex-col items-center gap-1 mb-4 text-center">
          <div className="p-2 rounded-full bg-[#a855f7]/10 mb-2 border border-[#a855f7]/20 animate-pulse-slow">
            <ShieldCheck size={28} className="text-[#a855f7]" />
          </div>
          <h1 className="text-lg font-bold uppercase tracking-wider text-[#e5e2e1]">
            {isFluxoBiometrico ? 'Confirmação Biométrica' : 'Solicitação de Acesso'}
          </h1>
          <p className="text-[10px] text-[#bac9cc] opacity-70 tracking-widest uppercase">
            Protocolo de Segurança Ativo
          </p>
        </header>

        {/* Main Content Area */}
        <div className="glass-panel flex flex-col p-5 rounded-[2rem] overflow-hidden">
          {/* Circular Progress Timer & Device Info */}
          <section className="flex flex-col items-center justify-center pt-2 pb-2 space-y-4">
            <div className="relative flex items-center justify-center">
              {/* Progress Ring */}
              <svg className="w-28 h-28" viewBox="0 0 100 100">
                <circle className="text-slate-100" cx="50" cy="50" fill="transparent" r="42" stroke="currentColor" strokeWidth="8" />
                <circle
                  className="progress-ring__circle text-[#a855f7]"
                  cx="50"
                  cy="50"
                  fill="transparent"
                  r="42"
                  stroke="currentColor"
                  strokeDasharray="263.89"
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                  strokeWidth="8"
                />
              </svg>
              {/* Icon in center */}
              <div className="absolute flex flex-col items-center">
                <span className="text-2xl font-bold text-[#0f172a]" id="timer-text">{countdown}</span>
                <span className="text-[9px] uppercase text-[#64748b] opacity-60 tracking-wider">segundos</span>
              </div>
            </div>
            
            <div className="flex items-center gap-3 bg-[#f8fafc] px-4 py-1.5 rounded-full border border-slate-200 text-[#334155]">
              {ctx.navegador && (
                <div className="flex items-center gap-1">
                  <Monitor size={13} className="text-[#3b82f6] shrink-0" />
                  <span className="text-[11px] font-medium max-w-[90px] sm:max-w-[150px] truncate block" title={`${cleanBrowserName(ctx.navegador)}${ctx.sistemaOp ? ` / ${ctx.sistemaOp}` : ''}`}>
                    {cleanBrowserName(ctx.navegador)} {ctx.sistemaOp ? `/ ${ctx.sistemaOp}` : ''}
                  </span>
                </div>
              )}
              {ctx.navegador && ctx.ip && <div className="h-3 w-[1px] bg-slate-200 shrink-0" />}
              {ctx.ip && (
                <div 
                  onClick={() => handleCopyIp(ctx.ip)}
                  className="relative flex items-center gap-1 cursor-pointer select-none active:scale-95 transition-transform group"
                  title="Clique para copiar o IP completo"
                >
                  <Globe size={13} className="text-[#ec4899] shrink-0" />
                  <span className="text-[11px] font-medium max-w-[90px] sm:max-w-[150px] truncate block">
                    {ctx.ip}
                  </span>
                  
                  {/* Tooltip */}
                  <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[#131313] border border-white/10 rounded-lg shadow-2xl text-[10px] text-white z-30 transition-all duration-200 pointer-events-none ${
                    copiedIp 
                      ? 'opacity-100 translate-y-0 scale-100' 
                      : 'opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 scale-95 group-hover:scale-100'
                  }`}>
                    <div className="flex flex-col items-center gap-0.5 max-w-[280px]">
                      {copiedIp ? (
                        <>
                          <span className="font-bold text-[#10b981]">✓ Copiado!</span>
                          <span className="font-mono text-[#bac9cc] break-all text-center">{ctx.ip}</span>
                        </>
                      ) : (
                        <>
                          <span className="font-bold text-[#a855f7]">Clique para copiar</span>
                          <span className="font-mono text-[#bac9cc] break-all text-center">{ctx.ip}</span>
                        </>
                      )}
                    </div>
                    {/* Tooltip Arrow */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#131313]" />
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-white/10 -z-10 translate-y-[1px]" />
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Configuration Section - Horizontal Layout */}
          {(!isFluxoBiometrico || showBiometriaFallback) && (
            requerVozDezenas ? (
              <section className="py-3">
                <p className="text-[#64748b] text-[10px] font-semibold uppercase tracking-[0.2em] mb-2.5 text-center opacity-85">
                  Validação por Dezenas
                </p>
                
                {/* Visual do Status da Voz / Microfone */}
                <div className="flex flex-col items-center justify-center p-3 mb-3 bg-slate-50 border border-slate-200/80 rounded-2xl text-center">
                  <div className="flex items-center gap-2 mb-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        speakChallenge();
                      }}
                      className={`p-2.5 rounded-full border transition-all ${
                        micActive
                          ? 'bg-red-50 border-red-200 text-red-500 animate-pulse'
                          : 'bg-[#a855f7]/10 border-[#a855f7]/20 text-[#a855f7] active:scale-95'
                      }`}
                      title="Clique para falar novamente"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-mic">
                        <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                        <path d="M19 10v1a7 7 0 0 1-14 0v-1"/>
                        <line x1="12" x2="12" y1="19" y2="22"/>
                      </svg>
                    </button>
                    <span className="text-[11px] font-bold text-slate-700">
                      {micActive ? 'Escutando sua voz...' : 'Microfone inativo'}
                    </span>
                  </div>
                  
                  {lastHeard && (
                    <p className="text-[10px] text-slate-500 italic mb-1">
                      Ouvido: "{lastHeard}"
                    </p>
                  )}
                  
                  {sttError ? (
                    <p className="text-[9px] text-red-500 font-semibold max-w-[280px]">
                      {sttError}
                    </p>
                  ) : (
                    <p className="text-[9px] text-[#a855f7] font-semibold">
                      Diga o número falado ou mostrado no computador
                    </p>
                  )}
                </div>

                {/* 3 Botões de Fallback */}
                <div className="flex gap-2">
                  {dezenasOpcoes.map((opVal) => (
                    <button
                      key={opVal}
                      type="button"
                      onClick={() => responder('AUTORIZADO', undefined, opVal)}
                      className="flex-1 py-4 bg-white active:scale-95 hover:bg-slate-50 border-2 border-slate-200 hover:border-[#a855f7] rounded-2xl text-lg font-extrabold text-slate-800 transition-all shadow-sm flex items-center justify-center"
                    >
                      {opVal}
                    </button>
                  ))}
                </div>
              </section>
            ) : (
              <section className="py-3">
                <p className="text-[#64748b] text-[10px] font-semibold uppercase tracking-[0.2em] mb-2.5 text-center opacity-85">Configuração de Acesso</p>
                <div className="flex gap-1.5 bg-slate-50 p-1 rounded-xl border border-slate-200/80">
                  {/* Opção 1: Temporário */}
                  <button
                    type="button"
                    onClick={() => setOpcao('TEMPORARIO')}
                    className={`option-card flex-1 flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-all ${
                      opcao === 'TEMPORARIO'
                        ? 'selection-active'
                        : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    <Key size={16} className={opcao === 'TEMPORARIO' ? 'text-[#a855f7]' : 'text-slate-400'} />
                    <span className="text-[10px] font-bold text-center leading-tight uppercase">Único</span>
                  </button>
                  
                  {/* Opção 2: Permanente */}
                  <button
                    type="button"
                    onClick={() => setOpcao('PERMANENTE')}
                    className={`option-card flex-1 flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-all ${
                      opcao === 'PERMANENTE'
                        ? 'selection-active'
                        : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    <Clock size={16} className={opcao === 'PERMANENTE' ? 'text-[#a855f7]' : 'text-slate-400'} />
                    <span className="text-[10px] font-bold text-center leading-tight uppercase">30 Dias</span>
                  </button>

                  {/* Opção 3: Passkey */}
                  <button
                    type="button"
                    onClick={() => setOpcao('PASSKEY')}
                    className={`option-card flex-1 flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-all ${
                      opcao === 'PASSKEY'
                        ? 'selection-active'
                        : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    <Fingerprint size={16} className={opcao === 'PASSKEY' ? 'text-[#a855f7]' : 'text-slate-400'} />
                    <span className="text-[10px] font-bold text-center leading-tight uppercase">Sempre</span>
                  </button>
                </div>
              </section>
            )
          )}

          {/* Action Buttons - Reordered */}
          <footer className="mt-5 space-y-3 relative">
            {error && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-[#ef4444] text-xs font-semibold text-center mb-3">
                {error}
              </div>
            )}
            {!requerVozDezenas && (
              <button
                onClick={() => responder('AUTORIZADO')}
                className="w-full bg-[#10b981] hover:brightness-110 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 shadow-[0_8px_25px_-5px_rgba(16,185,129,0.4)] active:scale-95 transition-all text-sm uppercase tracking-wider"
              >
                {isFluxoBiometrico ? <Fingerprint size={18} /> : <CheckCircle2 size={18} />}
                <span>{isFluxoBiometrico ? 'TOQUE PARA DIGITAL' : 'AUTORIZAR ACESSO'}</span>
              </button>
            )}
            
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => responder('NEGADO')}
                className="w-full bg-[#f1f5f9] hover:bg-[#e2e8f0] text-[#334155] font-bold py-3 rounded-xl active:scale-95 transition-all border border-slate-200/50 text-sm uppercase tracking-wider"
              >
                NEGAR
              </button>
              
              <div className="relative w-full">
                <button
                  type="button"
                  onClick={() => setShowBlockMenu(!showBlockMenu)}
                  className="w-full bg-[#ef4444] text-white font-bold py-3 rounded-xl active:scale-95 transition-all shadow-[0_8px_25px_-5px_rgba(255,0,0,0.4)] flex items-center justify-center gap-1.5 text-sm uppercase tracking-wider"
                >
                  <Ban size={15} />
                  <span>BLOQUEAR</span>
                </button>
                
                {showBlockMenu && (
                  <div className="absolute bottom-[110%] right-0 w-48 rounded-xl overflow-hidden border border-[#e2e8f0] bg-white shadow-[0_-8px_30px_rgba(0,0,0,0.15)] z-20">
                    <button
                      type="button"
                      onClick={() => responder('BLOQUEADO', 'PAGINA')}
                      className="w-full py-3 px-4 text-[#334155] hover:bg-slate-50 text-left text-xs font-semibold"
                    >
                      Bloquear apenas página
                    </button>
                    <div className="h-[1px] bg-[#e2e8f0]" />
                    <button
                      type="button"
                      onClick={() => responder('BLOQUEADO', 'COMPLETO')}
                      className="w-full py-3 px-4 text-[#ef4444] hover:bg-slate-50 text-left text-xs font-semibold"
                    >
                      Bloquear completo
                    </button>
                    <div className="h-[1px] bg-[#e2e8f0]" />
                    <button
                      type="button"
                      onClick={() => setShowBlockMenu(false)}
                      className="w-full py-2 px-4 text-[#64748b] hover:bg-slate-50 text-center text-xs font-bold"
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </div>
            </div>
          </footer>
        </div>
      </main>

      <style>{`
        .glass-panel {
          background: #ffffff;
          border: 1px solid rgba(0, 0, 0, 0.08);
          box-shadow: 0 25px 60px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04);
        }
        .progress-ring__circle {
          transition: stroke-dashoffset 1s linear;
          transform: rotate(-90deg);
          transform-origin: 50% 50%;
        }
        .selection-active {
          background: linear-gradient(135deg, rgba(168, 85, 247, 0.12), rgba(59, 130, 246, 0.12)) !important;
          border-color: #a855f7 !important;
          color: #7c3aed !important;
        }
        @keyframes pulse-ring {
          0% { transform: scale(0.97); opacity: 0.7; }
          50% { transform: scale(1.03); opacity: 0.95; }
          100% { transform: scale(0.97); opacity: 0.7; }
        }
        @keyframes prm-pan-bg {
          0%, 100% { background-position: 0% 50%; }
          50%      { background-position: 100% 50%; }
        }
        .animate-pulse-slow {
          animation: pulse-ring 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
//#endregion AC_BLOCK approve-page.approve-page

// ── Styles ──
const pageStyle: React.CSSProperties = {
  height: '100dvh', minHeight: '100dvh', display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center', padding: '16px',
  background: 'linear-gradient(-45deg, #0f0a1a, #1a1035, #0d1b2a, #151025)',
  backgroundSize: '400% 400%',
  animation: 'prm-pan-bg 18s ease infinite',
  color: '#e5e2e1',
  position: 'relative', overflow: 'hidden', boxSizing: 'border-box',
  fontFamily: 'Inter, sans-serif',
};

const infoRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  fontSize: '0.78rem', color: '#f1f5f9', padding: '4px 0',
};

const actionBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  padding: '10px 16px', borderRadius: 12, border: 'none',
  fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
  transition: 'all 0.2s',
};

const blockOptionStyle: React.CSSProperties = {
  width: '100%', padding: '12px 16px', border: 'none', background: 'transparent',
  color: '#fca5a5', fontSize: '0.84rem', fontWeight: 600, cursor: 'pointer',
  textAlign: 'left',
};

const secondaryCloseBtnStyle: React.CSSProperties = {
  marginTop: 20,
  padding: '12px 20px',
  borderRadius: 14,
  border: '1px solid rgba(255,255,255,0.08)',
  background: 'rgba(255,255,255,0.03)',
  color: '#94a3b8',
  fontSize: '0.85rem',
  fontWeight: 700,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  maxWidth: 340,
  transition: 'all 0.2s',
};

const appBannerStyle: React.CSSProperties = {
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: 16,
  padding: '12px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  maxWidth: 420,
  margin: '0 auto 16px',
  boxSizing: 'border-box',
};

const appIconStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 10,
  background: 'rgba(124, 58, 237, 0.15)',
  border: '1px solid rgba(124, 58, 237, 0.3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const appBannerBtnStyle: React.CSSProperties = {
  background: '#7c3aed',
  color: '#ffffff',
  border: 'none',
  padding: '6px 14px',
  borderRadius: 8,
  fontSize: '0.75rem',
  fontWeight: 800,
  cursor: 'pointer',
  boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)',
};
